import styles from "./Contacts.module.css";
import { useCallback, useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useWindowSize } from "react-use";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import {
  Button,
  Checkbubble,
  CircleLoader,
  SearchInput,
  AppHeader,
} from "@wwg/ui";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalControls,
} from "shared/components/Modal/Modal";
import { AddContactButton } from "shared/components/AddContactButton/AddContactButton";
import { ScrollBox } from "shared/components/ScrollBox/ScrollBox";
import { ContactEdit } from "shared/components/ContactEdit/ContactEdit";
import { useRetained } from "shared/hooks/useRetained";
import { joinPath } from "shared/utils/joinPath";
import { SORT_TYPE } from "shared/enums";
import { DetailsContact } from "./DetailsContact/DetailsContact";
import { DetailsIBO } from "./DetailsIBO/DetailsIBO";
import { ContactsList } from "./ContactsList/ContactsList";
import { ContactsContainer } from "./useContacts";

// Map of sort type to their i18n translation keys.
const SORT_TYPE_I18N = {
  [SORT_TYPE.NameAsc]: "sort.labelNameAsc",
  [SORT_TYPE.NameDesc]: "sort.labelNameDesc",
  [SORT_TYPE.DateAsc]: "sort.labelDateAsc",
  [SORT_TYPE.DateDesc]: "sort.labelDateDesc",
  [SORT_TYPE.PinAsc]: "sort.labelPinAsc",
  [SORT_TYPE.PinDesc]: "sort.labelPinDesc",
};

export function Contacts() {
  const { type, id } = useParams();

  return (
    <ContactsContainer.Provider initialState={{ archived: false }}>
      <ContactsPage
        path={"/contacts"}
        type={type}
        id={id != null ? Number(id) : null}
      />
    </ContactsContainer.Provider>
  );
}

export function ContactsPage({ path, type, id }) {
  const { t } = useTranslation();
  const {
    archived,
    searchTerm,
    setSearchTerm,
    sortType,
    setSortType,
    filterType,
    isLoading,
    isPreviousData,
    createContactMutation,
  } = ContactsContainer.useContainer();
  const history = useHistory();

  const isLoaded = type != null && id != null;
  const isNew = type === "new";

  // Keep the last record loaded.
  // This makes for a nicer transition when the page retains the old info when animating out.
  const _type = useRetained(type);
  const _id = useRetained(id);

  const [showSortOptions, setShowSortOptions] = useState(false);
  const [modalError, setModalError] = useState(null);

  useEffect(() => {
    if (
      filterType === "ibos" &&
      (sortType === SORT_TYPE.DateAsc || sortType === SORT_TYPE.DateDesc)
    ) {
      setSortType(SORT_TYPE.NameAsc);
    }
  }, [filterType, sortType, setSortType]);

  return (
    <div className={styles.container}>
      {!archived && (
        <AppHeader>
          <AddContactButton
            disabled={isNew}
            onClick={() => {
              history.push("/contacts/new");
            }}
          />
        </AppHeader>
      )}

      <div className={classNames(styles.searchBar, styles.desktopSearch)}>
        <SearchInput
          placeholder={t("labels.searchPlaceholder")}
          spellCheck={false}
          value={searchTerm}
          onSearch={(term) => {
            setSearchTerm(term);
          }}
          timeout={300}
        />

        <Button
          onClick={() => {
            setShowSortOptions(true);
          }}
        >
          {t("buttons.sort")}
        </Button>
      </div>

      <div className={styles.mainContent}>
        <div
          className={classNames(styles.page, {
            [styles.showDetails]: isLoaded || isNew,
          })}
        >
          <div className={styles.listContainer}>
            <ScrollBox className={styles.list}>
              <div
                className={classNames(styles.searchBar, styles.mobileSearch)}
              >
                <SearchInput
                  placeholder={t("labels.searchPlaceholder")}
                  spellCheck={false}
                  value={searchTerm}
                  onSearch={(term) => {
                    setSearchTerm(term);
                  }}
                  timeout={300}
                />
                <Button
                  onClick={() => {
                    setShowSortOptions(true);
                  }}
                >
                  {t("buttons.sort")}
                </Button>
              </div>
              <ContactsList
                selectedContactId={id}
                linkBase={path}
                searchTerm={searchTerm}
              />
            </ScrollBox>

            <div
              className={classNames(styles.loadingShade, {
                [styles.isLoading]: isLoading || isPreviousData,
              })}
            >
              <CircleLoader isLoading={isLoading || isPreviousData} />
            </div>
          </div>

          <ScrollBox className={styles.details}>
            {isNew ? (
              <ContactEdit
                contact={{
                  id: null,
                  name: "",
                  phone: "",
                  email: "",
                  type: 0,
                }}
                onCancel={() => {
                  // Go back to where the user clicked the Add Contact button,
                  // otherwise fall back to contacts list.
                  if (history.length > 0) {
                    history.goBack();
                  } else {
                    history.push("/contacts");
                  }
                }}
                onSave={(contact) => {
                  createContactMutation
                    .mutateAsync(contact)
                    .then(async (res) => {
                      const json = await res.json();
                      if (res.ok) {
                        return json.data;
                      } else {
                        throw new Error(json.message);
                      }
                    })
                    .then((data) => {
                      if (data.contactId) {
                        history.push(`/contacts/customers/${data.contactId}`);
                      }
                    })
                    .catch(setModalError);
                }}
                onArchive={() => {}}
              />
            ) : _type === "customers" ? (
              <CustomersRoute linkBase={path} id={_id} isLoaded={isLoaded} />
            ) : _type === "ibos" ? (
              <IBOsRoute linkBase={path} id={_id} isLoaded={isLoaded} />
            ) : (
              <span className={styles.emptyLabel}>
                {t("labels.noContactSelected")}
              </span>
            )}
          </ScrollBox>
        </div>
      </div>

      <Modal isOpen={modalError}>
        <ModalContent>
          <p>{modalError?.message}</p>
        </ModalContent>
        <ModalControls>
          <Button
            onClick={() => {
              setModalError(null);
            }}
          >
            {t("ok")}
          </Button>
        </ModalControls>
      </Modal>

      <Modal isOpen={showSortOptions}>
        <ModalContent>
          <ModalHeader>{t("sort.modalHeader")}</ModalHeader>
          <ul className={styles.sortOptionsList}>
            {[
              SORT_TYPE.DateDesc,
              SORT_TYPE.DateAsc,
              SORT_TYPE.NameAsc,
              SORT_TYPE.NameDesc,
              !archived && SORT_TYPE.PinDesc,
              !archived && SORT_TYPE.PinAsc,
            ]
              .filter((x) => x)
              .map((value) => (
                <li key={value}>
                  <Checkbubble
                    isChecked={sortType === value}
                    onChange={() => {
                      setSortType(value);
                    }}
                    isDisabled={
                      filterType == "ibos" &&
                      (value == "date-asc" || value == "date-desc")
                    }
                  />
                  {t(SORT_TYPE_I18N[value])}
                </li>
              ))}
          </ul>
        </ModalContent>
        <ModalControls>
          <Button
            buttonStyle="default"
            onClick={() => {
              setShowSortOptions(false);
            }}
          >
            {t("buttons.close")}
          </Button>
        </ModalControls>
      </Modal>
    </div>
  );
}

function CustomersRoute({ linkBase = "/contacts", id, isLoaded }) {
  const history = useHistory();
  const { width } = useWindowSize();

  const onBack = useCallback(() => {
    history.push(linkBase);
  }, [linkBase]);

  return (
    <>
      <AppHeader onBack={width < 800 && isLoaded ? onBack : null} />
      <DetailsContact id={id} linkBase={joinPath(linkBase, "customers", id)} />
    </>
  );
}

function IBOsRoute({ linkBase = "/contacts", id, isLoaded }) {
  const history = useHistory();
  const { width } = useWindowSize();

  const onBack = useCallback(() => {
    history.push(linkBase);
  }, [linkBase]);

  return (
    <>
      <AppHeader onBack={width < 800 && isLoaded ? onBack : null} />
      <DetailsIBO id={id} linkBase={joinPath(linkBase, "ibos", id)} />
    </>
  );
}
